import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    alpha,
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    FormControlLabel,
    Grid,
    IconButton,
    Paper,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import {
    fetchSchoolPage,
    saveSchoolPage,
    publishPage,
    unpublishPage,
} from "../../../redux/schoolPageRelated/schoolPageHandle";
import { underPageControl } from "../../../redux/schoolPageRelated/schoolPageSlice";
import Popup from "../../../components/Popup";
import {
    isValidPublicSlug,
    normalizePublicSlug,
    publicSchoolLiveUrl,
    slugifySchoolName,
} from "../../../utils/schoolSlug";
import {
    getPlatformHost,
    getSiteOrigin,
    isSchoolSubdomainsEnabled,
    usePlatformHomeSchoolOnApex,
} from "../../../config/platformHost";
import { publicSchoolPath } from "../../../utils/schoolSlug";
import { APP_NAME } from "../../../config/brand";

const ABOUT_MAX = 800;

function parseKeyFacts(raw) {
    if (Array.isArray(raw)) return raw.map((f) => ({ label: f.label || "", value: f.value || "" }));
    return [];
}

const SchoolPageBuilder = () => {
    const dispatch = useDispatch();
    const { currentUser } = useSelector((s) => s.user);
    const { schoolPage, loading, statestatus, response, error } = useSelector((s) => s.schoolPage);

    const schoolId = currentUser?._id;
    const schoolName = currentUser?.schoolName || "Your school";

    const [showPopup, setShowPopup] = useState(false);
    const [message, setMessage] = useState("");
    const [popupSeverity, setPopupSeverity] = useState(undefined);
    const [copyHint, setCopyHint] = useState("");

    const [heroHeadline, setHeroHeadline] = useState("");
    const [heroTagline, setHeroTagline] = useState("");
    const [heroCtaText, setHeroCtaText] = useState("Apply for admission");
    const [heroCtaLink, setHeroCtaLink] = useState("");
    const [accentColor, setAccentColor] = useState("#1a535c");

    const [aboutTitle, setAboutTitle] = useState("About our school");
    const [aboutBody, setAboutBody] = useState("");
    const [foundedYear, setFoundedYear] = useState("");
    const [motto, setMotto] = useState("");
    const [vision, setVision] = useState("");
    const [mission, setMission] = useState("");

    const [keyFacts, setKeyFacts] = useState([]);

    const [phone, setPhone] = useState("");
    const [email, setEmail] = useState("");
    const [address, setAddress] = useState("");
    const [whatsappNumber, setWhatsappNumber] = useState("");
    const [facebookUrl, setFacebookUrl] = useState("");

    const [showStats, setShowStats] = useState(true);
    const [showAbout, setShowAbout] = useState(true);
    const [showContact, setShowContact] = useState(true);
    const [showAnnouncements, setShowAnnouncements] = useState(true);

    const [servesAsPlatformHome, setServesAsPlatformHome] = useState(false);
    const [publicSlug, setPublicSlug] = useState("");

    const slugPreview = normalizePublicSlug(publicSlug) || slugifySchoolName(schoolName);
    const slugValid = isValidPublicSlug(slugPreview);

    const publicUrl = useMemo(() => {
        if (!slugPreview) return "";
        return publicSchoolLiveUrl(slugPreview, { preview: true });
    }, [slugPreview]);

    const publishedUrl = useMemo(() => {
        if (!slugPreview) return "";
        return publicSchoolLiveUrl(slugPreview);
    }, [slugPreview]);

    const platformHost = getPlatformHost();
    const siteOrigin = getSiteOrigin();
    const useSubdomains = isSchoolSubdomainsEnabled();
    const allowPlatformHomeOnRoot = usePlatformHomeSchoolOnApex();
    const schoolPublicPath = slugPreview ? `${siteOrigin}${publicSchoolPath(slugPreview)}` : "";

    const isPublished = Boolean(schoolPage?.isPublished);

    const hydrateFromPage = useCallback(
        (page) => {
            if (!page) return;
            setHeroHeadline(page.heroHeadline || "");
            setHeroTagline(page.heroTagline || "");
            setHeroCtaText(page.heroCtaText || "Apply for admission");
            setHeroCtaLink(page.heroCtaLink || "");
            setAccentColor(page.accentColor || "#1a535c");
            setAboutTitle(page.aboutTitle || "About our school");
            setAboutBody((page.aboutBody || "").slice(0, ABOUT_MAX));
            setFoundedYear(page.foundedYear || "");
            setMotto(page.motto || "");
            setVision(page.vision || "");
            setMission(page.mission || "");
            setKeyFacts(parseKeyFacts(page.keyFacts));
            setPhone(page.phone || "");
            setEmail(page.email || "");
            setAddress(page.address || "");
            setWhatsappNumber(page.whatsappNumber || "");
            setFacebookUrl(page.facebookUrl || "");
            setShowStats(page.showStats !== false);
            setShowAbout(page.showAbout !== false);
            setShowContact(page.showContact !== false);
            setShowAnnouncements(page.showAnnouncements !== false);
            setServesAsPlatformHome(page.servesAsPlatformHome === true);
            setPublicSlug(page.publicSlug || "");
        },
        []
    );

    useEffect(() => {
        if (!schoolId) return;
        dispatch(fetchSchoolPage(schoolId));
        return () => {
            dispatch(underPageControl());
        };
    }, [dispatch, schoolId]);

    useEffect(() => {
        if (schoolPage) {
            hydrateFromPage(schoolPage);
        }
    }, [schoolPage, hydrateFromPage]);

    useEffect(() => {
        if (statestatus === "added") {
            setMessage("Changes saved successfully");
            setPopupSeverity("success");
            setShowPopup(true);
            dispatch(underPageControl());
            if (schoolId) {
                dispatch(fetchSchoolPage(schoolId));
            }
        } else if (statestatus === "failed") {
            const msg =
                typeof response === "string" && response
                    ? response
                    : typeof error === "string" && error
                      ? error
                      : error?.response?.data?.message || error?.message || "Request failed";
            setMessage(msg);
            setPopupSeverity("error");
            setShowPopup(true);
            dispatch(underPageControl());
        }
    }, [statestatus, response, error, dispatch, schoolId]);

    const handleCopyLink = async () => {
        const link = isPublished ? publishedUrl : publicUrl;
        if (!link) return;
        try {
            await navigator.clipboard.writeText(link);
            setCopyHint("Copied!");
            setTimeout(() => setCopyHint(""), 2000);
        } catch {
            setCopyHint("Could not copy");
        }
    };

    const saveSection = (fields) => {
        if (!schoolId) return;
        dispatch(saveSchoolPage(schoolId, fields));
    };

    const handleVisibilityChange = (field, checked) => {
        if (field === "showStats") setShowStats(checked);
        if (field === "showAbout") setShowAbout(checked);
        if (field === "showContact") setShowContact(checked);
        if (field === "showAnnouncements") setShowAnnouncements(checked);
        saveSection({ [field]: checked });
    };

    const updateKeyFact = (index, key, value) => {
        setKeyFacts((prev) =>
            prev.map((row, i) => (i === index ? { ...row, [key]: value } : row))
        );
    };

    const addKeyFact = () => {
        setKeyFacts((prev) => [...prev, { label: "", value: "" }]);
    };

    const removeKeyFact = (index) => {
        setKeyFacts((prev) => prev.filter((_, i) => i !== index));
    };

    const previewHeadline = heroHeadline.trim() || schoolName;
    const studentCount = schoolPage?.studentCount ?? 0;
    const classCount = schoolPage?.classCount ?? 0;
    const teacherCount = schoolPage?.teacherCount ?? 0;

    const validKeyFacts = keyFacts.filter((f) => f.label?.trim() || f.value?.trim());

    if (!schoolId) {
        return (
            <Container sx={{ py: 4 }}>
                <Typography color="text.secondary">Sign in as an administrator to edit the school page.</Typography>
            </Container>
        );
    }

    return (
        <Box
            component="main"
            sx={{
                minHeight: "100%",
                pb: 6,
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.04)} 0%, ${t.palette.grey[50]} 24%)`
                        : alpha(t.palette.common.black, 0.12),
            }}
        >
            <Container maxWidth="xl" sx={{ pt: { xs: 2, sm: 3 } }}>
                <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={2}
                    alignItems={{ md: "flex-start" }}
                    justifyContent="space-between"
                    sx={{ mb: 3 }}
                >
                    <Box>
                        <Typography variant="h4" fontWeight={800} gutterBottom>
                            School Public Page Builder
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Customise the public-facing page parents and students see
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                        <Chip
                            label={isPublished ? "Live" : "Hidden"}
                            color={isPublished ? "success" : "default"}
                            size="small"
                            sx={{ fontWeight: 700 }}
                        />
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<OpenInNewIcon />}
                            href={publicUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            View Live Page
                        </Button>
                        <Tooltip title={copyHint || "Copy public link"}>
                            <IconButton size="small" onClick={handleCopyLink} aria-label="Copy link">
                                <ContentCopyIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        {isPublished ? (
                            <Button
                                variant="outlined"
                                color="warning"
                                size="small"
                                disabled={loading}
                                onClick={() => dispatch(unpublishPage(schoolId))}
                            >
                                Unpublish
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                color="success"
                                size="small"
                                onClick={() =>
                                    dispatch(
                                        publishPage(
                                            schoolId,
                                            normalizePublicSlug(publicSlug) || slugifySchoolName(schoolName),
                                        ),
                                    )
                                }
                                disabled={loading || !slugValid}
                            >
                                Publish Page
                            </Button>
                        )}
                    </Stack>
                </Stack>

                <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                    <Typography variant="body2" component="div" sx={{ mb: useSubdomains ? 1 : 0 }}>
                        <strong>Guide:</strong> Edit your school’s public page, save changes, then press <strong>Publish</strong> when
                        parents should see it. You will get a clear success or error message after each action.
                    </Typography>
                    {useSubdomains ? (
                        <>
                            The main {APP_NAME} site stays at <strong>www.{platformHost}</strong>. After you publish, parents
                            open your school at <strong>{slugPreview || "your-slug"}.{platformHost}</strong> (set the subdomain
                            slug below). Register more schools from <strong>www.{platformHost}</strong> — each gets its own
                            subdomain and admin login.
                        </>
                    ) : (
                        <>
                            The main site is at <strong>{siteOrigin}</strong>. After you publish, parents open your school at{" "}
                            <strong>
                                {schoolPublicPath || `${siteOrigin}/school/your-slug`}
                            </strong>{" "}
                            (set the URL slug below). Register more schools from <strong>{siteOrigin}</strong>.
                        </>
                    )}
                </Alert>

                {loading && !schoolPage ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                                    Public page URL
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    {useSubdomains
                                        ? `This slug becomes your subdomain: ${slugPreview || "slug"}.${platformHost}`
                                        : `Each school gets a path on your site, e.g. ${siteOrigin}/school/your-slug`}
                                </Typography>
                                {publishedUrl ? (
                                    <Typography variant="body2" sx={{ mb: 2, fontFamily: "monospace", fontSize: 13 }}>
                                        {publishedUrl}
                                    </Typography>
                                ) : null}
                                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "flex-start" }}>
                                    <TextField
                                        label="URL slug"
                                        fullWidth
                                        value={publicSlug}
                                        onChange={(e) => setPublicSlug(normalizePublicSlug(e.target.value))}
                                        placeholder={slugifySchoolName(schoolName)}
                                        helperText={
                                            slugValid
                                                ? useSubdomains
                                                    ? `Live site: ${slugPreview}.${platformHost}`
                                                    : `Live site: ${schoolPublicPath}`
                                                : "Use 3–80 characters: lowercase letters, numbers, hyphens"
                                        }
                                        error={Boolean(publicSlug) && !slugValid}
                                    />
                                    <Button
                                        variant="outlined"
                                        sx={{ flexShrink: 0, mt: { xs: 0, sm: 0.5 } }}
                                        onClick={() =>
                                            saveSection({
                                                publicSlug: slugValid ? slugPreview : normalizePublicSlug(publicSlug),
                                            })
                                        }
                                        disabled={!slugValid}
                                    >
                                        Save slug
                                    </Button>
                                </Stack>
                            </Paper>
                        </Grid>
                        {!useSubdomains ? (
                            <Grid item xs={12}>
                                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                                        Site home page (/)
                                    </Typography>
                                    {allowPlatformHomeOnRoot ? (
                                        <>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={servesAsPlatformHome}
                                                        onChange={(e) => {
                                                            const checked = e.target.checked;
                                                            setServesAsPlatformHome(checked);
                                                            saveSection({ servesAsPlatformHome: checked });
                                                        }}
                                                    />
                                                }
                                                label="Use this school as the platform home page"
                                            />
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                                When published, visitors opening the root URL see this school&apos;s page instead of the
                                                main landing. With subdomains enabled (production), use www.{platformHost} for the main
                                                site instead.
                                            </Typography>
                                        </>
                                    ) : (
                                        <Alert severity="info" sx={{ mt: 0.5 }}>
                                            On <strong>{platformHost}</strong>, the root URL always shows the main {APP_NAME}
                                            landing (<strong>{siteOrigin}</strong>). Parents open this school at{" "}
                                            <strong>{schoolPublicPath || `${siteOrigin}/school/your-slug`}</strong>.
                                        </Alert>
                                    )}
                                </Paper>
                            </Grid>
                        ) : null}
                        <Grid item xs={12} lg={8}>
                            <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
                                <Typography variant="h6" fontWeight={700} gutterBottom>
                                    Hero Section
                                </Typography>
                                <Stack spacing={2}>
                                    <TextField
                                        label="Hero Headline"
                                        required
                                        fullWidth
                                        value={heroHeadline}
                                        onChange={(e) => setHeroHeadline(e.target.value)}
                                        placeholder={schoolName}
                                        helperText="Main heading visitors see first"
                                    />
                                    <TextField
                                        label="Hero Tagline"
                                        fullWidth
                                        multiline
                                        rows={2}
                                        value={heroTagline}
                                        onChange={(e) => setHeroTagline(e.target.value)}
                                        helperText="One sentence about your school"
                                    />
                                    <TextField
                                        label="CTA Button Text"
                                        fullWidth
                                        value={heroCtaText}
                                        onChange={(e) => setHeroCtaText(e.target.value)}
                                    />
                                    <TextField
                                        label="CTA Button Link"
                                        fullWidth
                                        value={heroCtaLink}
                                        onChange={(e) => setHeroCtaLink(e.target.value)}
                                        helperText="e.g. mailto:admissions@school.mw or a phone number"
                                    />
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <Box
                                            component="input"
                                            type="color"
                                            value={accentColor}
                                            onChange={(e) => setAccentColor(e.target.value)}
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                p: 0,
                                                border: "1px solid",
                                                borderColor: "divider",
                                                borderRadius: 1,
                                                cursor: "pointer",
                                            }}
                                            aria-label="Page accent colour"
                                        />
                                        <Typography variant="body2" color="text.secondary">
                                            Page accent colour
                                        </Typography>
                                    </Stack>
                                    <Button
                                        variant="contained"
                                        startIcon={<SaveOutlinedIcon />}
                                        disabled={loading}
                                        onClick={() =>
                                            saveSection({
                                                heroHeadline,
                                                heroTagline,
                                                heroCtaText,
                                                heroCtaLink,
                                                accentColor,
                                            })
                                        }
                                        sx={{ alignSelf: "flex-start" }}
                                    >
                                        Save Changes
                                    </Button>
                                </Stack>
                            </Paper>

                            <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
                                <Typography variant="h6" fontWeight={700} gutterBottom>
                                    About the School
                                </Typography>
                                <Stack spacing={2}>
                                    <TextField
                                        label="About Title"
                                        fullWidth
                                        value={aboutTitle}
                                        onChange={(e) => setAboutTitle(e.target.value)}
                                    />
                                    <TextField
                                        label="School Description"
                                        fullWidth
                                        multiline
                                        rows={5}
                                        value={aboutBody}
                                        onChange={(e) => setAboutBody(e.target.value.slice(0, ABOUT_MAX))}
                                        helperText={`Describe your school — history, achievements, values (max 800 characters) · ${aboutBody.length}/${ABOUT_MAX}`}
                                    />
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                label="Founded Year"
                                                fullWidth
                                                value={foundedYear}
                                                onChange={(e) => setFoundedYear(e.target.value)}
                                                placeholder="1982"
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                label="School Motto"
                                                fullWidth
                                                value={motto}
                                                onChange={(e) => setMotto(e.target.value)}
                                                placeholder="Excellence in Service"
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                label="Vision"
                                                fullWidth
                                                value={vision}
                                                onChange={(e) => setVision(e.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                label="Mission"
                                                fullWidth
                                                value={mission}
                                                onChange={(e) => setMission(e.target.value)}
                                            />
                                        </Grid>
                                    </Grid>
                                    <Button
                                        variant="contained"
                                        startIcon={<SaveOutlinedIcon />}
                                        disabled={loading}
                                        onClick={() =>
                                            saveSection({
                                                aboutTitle,
                                                aboutBody,
                                                foundedYear,
                                                motto,
                                                vision,
                                                mission,
                                            })
                                        }
                                        sx={{ alignSelf: "flex-start" }}
                                    >
                                        Save Changes
                                    </Button>
                                </Stack>
                            </Paper>

                            <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
                                <Typography variant="h6" fontWeight={700} gutterBottom>
                                    Key Facts
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    These appear as highlight cards on your page. Live student, class, and teacher
                                    counts are added automatically when &quot;Show student/class/teacher counts&quot; is on.
                                </Typography>
                                <Stack spacing={1.5} sx={{ mb: 2 }}>
                                    {keyFacts.map((fact, index) => (
                                        <Stack key={index} direction="row" spacing={1} alignItems="center">
                                            <TextField
                                                label="Label"
                                                size="small"
                                                value={fact.label}
                                                onChange={(e) => updateKeyFact(index, "label", e.target.value)}
                                                sx={{ flex: 1 }}
                                            />
                                            <TextField
                                                label="Value"
                                                size="small"
                                                value={fact.value}
                                                onChange={(e) => updateKeyFact(index, "value", e.target.value)}
                                                sx={{ flex: 1 }}
                                            />
                                            <IconButton
                                                color="error"
                                                size="small"
                                                onClick={() => removeKeyFact(index)}
                                                aria-label="Delete fact"
                                            >
                                                <DeleteOutlineIcon />
                                            </IconButton>
                                        </Stack>
                                    ))}
                                </Stack>
                                <Stack direction="row" spacing={1}>
                                    <Button startIcon={<AddIcon />} onClick={addKeyFact} size="small">
                                        Add Fact
                                    </Button>
                                    <Button
                                        variant="contained"
                                        startIcon={<SaveOutlinedIcon />}
                                        disabled={loading}
                                        onClick={() => saveSection({ keyFacts: validKeyFacts })}
                                    >
                                        Save Changes
                                    </Button>
                                </Stack>
                            </Paper>

                            <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
                                <Typography variant="h6" fontWeight={700} gutterBottom>
                                    Contact Details
                                </Typography>
                                <Stack spacing={2}>
                                    <TextField
                                        label="Phone"
                                        fullWidth
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                        helperText="e.g. +265 888 123 456"
                                    />
                                    <TextField
                                        label="Email"
                                        type="email"
                                        fullWidth
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                    />
                                    <TextField
                                        label="Physical Address"
                                        fullWidth
                                        multiline
                                        rows={2}
                                        value={address}
                                        onChange={(e) => setAddress(e.target.value)}
                                        helperText="P.O. Box or street address — shown on contact section"
                                    />
                                    <TextField
                                        label="WhatsApp Number"
                                        fullWidth
                                        value={whatsappNumber}
                                        onChange={(e) => setWhatsappNumber(e.target.value)}
                                        helperText="With country code: +265 888 123 456"
                                    />
                                    <TextField
                                        label="Facebook Page URL"
                                        fullWidth
                                        value={facebookUrl}
                                        onChange={(e) => setFacebookUrl(e.target.value)}
                                    />
                                    <Button
                                        variant="contained"
                                        startIcon={<SaveOutlinedIcon />}
                                        disabled={loading}
                                        onClick={() =>
                                            saveSection({
                                                phone,
                                                email,
                                                address,
                                                whatsappNumber,
                                                facebookUrl,
                                            })
                                        }
                                        sx={{ alignSelf: "flex-start" }}
                                    >
                                        Save Changes
                                    </Button>
                                </Stack>
                            </Paper>

                            <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
                                <Typography variant="h6" fontWeight={700} gutterBottom>
                                    Section Visibility
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Choose which sections appear on your public page
                                </Typography>
                                <Stack>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={showStats}
                                                onChange={(e) =>
                                                    handleVisibilityChange("showStats", e.target.checked)
                                                }
                                            />
                                        }
                                        label="Show student/class/teacher counts"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={showAbout}
                                                onChange={(e) =>
                                                    handleVisibilityChange("showAbout", e.target.checked)
                                                }
                                            />
                                        }
                                        label="Show About section"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={showContact}
                                                onChange={(e) =>
                                                    handleVisibilityChange("showContact", e.target.checked)
                                                }
                                            />
                                        }
                                        label="Show Contact section"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={showAnnouncements}
                                                onChange={(e) =>
                                                    handleVisibilityChange("showAnnouncements", e.target.checked)
                                                }
                                            />
                                        }
                                        label="Show latest school notices"
                                    />
                                </Stack>
                            </Paper>
                        </Grid>

                        <Grid item xs={12} lg={4}>
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 2,
                                    position: { lg: "sticky" },
                                    top: { lg: 80 },
                                }}
                            >
                                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                                    Live preview
                                </Typography>
                                <Box sx={{ bgcolor: "grey.100", borderRadius: 1, p: 0.75, mb: 1.5 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-all" }}>
                                        {publicUrl}
                                    </Typography>
                                </Box>

                                <Box
                                    sx={{
                                        bgcolor: alpha(accentColor, 0.12),
                                        p: 2,
                                        borderRadius: 1,
                                        mb: 1.5,
                                    }}
                                >
                                    <Typography variant="h6" fontWeight={700} sx={{ color: accentColor }}>
                                        {previewHeadline}
                                    </Typography>
                                    {heroTagline ? (
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                            {heroTagline}
                                        </Typography>
                                    ) : null}
                                    <Button
                                        size="small"
                                        variant="contained"
                                        sx={{
                                            mt: 1.5,
                                            bgcolor: accentColor,
                                            "&:hover": { bgcolor: accentColor, filter: "brightness(0.92)" },
                                        }}
                                    >
                                        {heroCtaText || "Apply for admission"}
                                    </Button>
                                </Box>

                                {showStats ? (
                                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                                        <Chip size="small" label={`${studentCount} Students`} variant="outlined" />
                                        <Chip size="small" label={`${classCount} Classes`} variant="outlined" />
                                        <Chip size="small" label={`${teacherCount} Teachers`} variant="outlined" />
                                    </Stack>
                                ) : null}

                                {showAbout && aboutBody.trim() ? (
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                                        {aboutBody.trim().slice(0, 100)}
                                        {aboutBody.trim().length > 100 ? "…" : ""}
                                    </Typography>
                                ) : null}

                                {validKeyFacts.length > 0 ? (
                                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                                        {validKeyFacts.map((fact, i) => (
                                            <Chip
                                                key={i}
                                                size="small"
                                                label={`${fact.value} ${fact.label}`.trim()}
                                                sx={{ bgcolor: alpha(accentColor, 0.08) }}
                                            />
                                        ))}
                                    </Stack>
                                ) : null}

                                {showContact && (phone || email || address) ? (
                                    <Stack spacing={0.25} sx={{ mb: 1.5 }}>
                                        {phone ? (
                                            <Typography variant="caption" display="block">
                                                {phone}
                                            </Typography>
                                        ) : null}
                                        {email ? (
                                            <Typography variant="caption" display="block">
                                                {email}
                                            </Typography>
                                        ) : null}
                                        {address ? (
                                            <Typography variant="caption" display="block" color="text.secondary">
                                                {address}
                                            </Typography>
                                        ) : null}
                                    </Stack>
                                ) : null}

                                <Button
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    startIcon={<OpenInNewIcon />}
                                    disabled={!publicUrl}
                                    onClick={() => {
                                        if (publicUrl) {
                                            window.open(publicUrl, "_blank", "noopener,noreferrer");
                                        }
                                    }}
                                >
                                    Open full preview
                                </Button>
                            </Paper>
                        </Grid>
                    </Grid>
                )}
            </Container>
            <Popup
                message={message}
                setShowPopup={setShowPopup}
                showPopup={showPopup}
                severity={popupSeverity}
            />
        </Box>
    );
};

export default SchoolPageBuilder;
